import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ERROR } from 'types';
import {
    CollabDeletionRequestsFilters,
    CollabDeletionRequestsSlice,
} from 'types/collaboration-deletion-request';
import { fetchCollaborationDeletionRequestThunk, fetchCollaborationDeletionRequestsThunk } from './thunk';

export const defaultDeletionRequestFilters: CollabDeletionRequestsFilters = {
    page: 1,
    q: '',
    campaign_id: undefined,
    campaign: null,
    country: '',
    start_date: '',
    end_date: '',
    status: '',
};

const initialState: CollabDeletionRequestsSlice = {
    loading: false,
    error: null,
    filters: defaultDeletionRequestFilters,
    applied_filter_id: null,
    filtered_lists: {},
    detailsLoading: false,
    details: {},
};

const collaborationDeletionRequestsSlice = createSlice({
    name: 'collaborationDeletionRequests',
    initialState,
    reducers: {
        updateDeletionRequestFilters: (
            state,
            { payload }: PayloadAction<CollabDeletionRequestsFilters>
        ) => {
            state.filters = {
                ...defaultDeletionRequestFilters,
                ...payload,
                page: payload.page ?? 1,
            };
        },
        expireDeletionRequestDetail: (
            state,
            { payload }: PayloadAction<{ cacheKey: string }>
        ) => {
            if (state.details[payload.cacheKey]) {
                state.details[payload.cacheKey] = {
                    ...state.details[payload.cacheKey],
                    expired: true,
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCollaborationDeletionRequestsThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCollaborationDeletionRequestsThunk.fulfilled, (state, { payload }) => {
                const { count, items, filterId, filters } = payload;
                const existingList = state.filtered_lists?.[filterId]?.list ?? [];
                const list = filters.page === 1 ? items : [...existingList, ...items];

                state.filtered_lists = {
                    ...state.filtered_lists,
                    [filterId]: {
                        list,
                        page: filters.page,
                        expired: false,
                        last_fetched_at: Date.now(),
                        has_more: count > list.length,
                        count,
                    },
                };
                state.applied_filter_id = filterId;
                state.loading = false;
                state.error = null;
            })
            .addCase(fetchCollaborationDeletionRequestsThunk.rejected, (state, { payload }) => {
                state.loading = false;
                if (payload !== ERROR.CACHED) {
                    state.error = payload || 'Failed to fetch deletion requests';
                }
            })
            .addCase(fetchCollaborationDeletionRequestThunk.pending, (state) => {
                state.detailsLoading = true;
            })
            .addCase(fetchCollaborationDeletionRequestThunk.fulfilled, (state, { payload }) => {
                const { cacheKey, item } = payload;
                state.details[cacheKey] = {
                    item,
                    last_fetched_at: Date.now(),
                    expired: false,
                };
                state.detailsLoading = false;
            })
            .addCase(fetchCollaborationDeletionRequestThunk.rejected, (state) => {
                state.detailsLoading = false;
            });
    },
});

export const { updateDeletionRequestFilters, expireDeletionRequestDetail } = collaborationDeletionRequestsSlice.actions;
export default collaborationDeletionRequestsSlice.reducer;
